<template>
    <v-dialog v-model="dialog" fullscreen hide-overlay transition="dialog-bottom-transition">
        <v-card class="feedback-fullscreen-card">
            <v-toolbar flat dense>
                <p role-type="body-3" class="ml-2">View Feedback</p>
            </v-toolbar>
            <div class="feedback-fixed-top px-6">
                <v-card class="mx-auto w-100 pa-4 mb-4 feedback-summary-card" outlined>
                    <div class="pa-0">
                        <v-row class="align-center">
                            <v-col class="feedback-summary-col">
                                <div class="d-flex align-center">
                                    <v-avatar size="69">
                                        <v-img :src="proctoringPic || defaultProfilePic"></v-img>
                                    </v-avatar>
                                    <ul class="feedback-user-list">
                                        <li class="text-body-2 font-weight-bold">{{ personalInfo?.firstName }} {{ personalInfo?.lastName }}</li>
                                        <li class="text-body-2 font-weight-regular">{{ personalInfo?.contactInfo?.email }}</li>
                                        <li class="text-body-2 font-weight-regular">{{ personalInfo?.contactInfo?.phoneNumber }}</li>
                                    </ul>
                                </div>
                            </v-col>
                            <v-col class="feedback-summary-col">
                                <div class="d-flex flex-column w-90">
                                    <div>
                                        <span class="text-body-2 font-weight-regular">School - </span>
                                        <span class="text-body-2 font-weight-bold float-right">
                                            {{ interviewDetails?.interviewSlotDetails?.[0]?.schoolName || '-' }}
                                        </span>
                                    </div>
                                    <div>
                                        <span class="text-body-2 font-weight-regular">Current CTC - </span>
                                        <span class="text-body-2 font-weight-bold float-right">
                                            {{ interviewDetails?.candidateDetails?.currentCTC ?? 'NA' }} LPA
                                        </span>
                                    </div>
                                    <div>
                                        <span class="text-body-2 font-weight-regular">Expected CTC - </span>
                                        <span class="text-body-2 font-weight-bold float-right">
                                            {{ interviewDetails?.candidateDetails?.expectedCTC ?? 'NA' }} LPA
                                        </span>
                                    </div>
                                </div>
                            </v-col>
                            <v-col class="feedback-summary-col">
                                <div class="d-flex flex-column w-90">
                                    <div>
                                        <span class="text-body-2 font-weight-regular">Total exp - </span>
                                        <span class="text-body-2 font-weight-bold float-right">
                                            {{ interviewDetails?.candidateDetails?.totalExperience?.years || '0 Year' }}
                                            {{ interviewDetails?.candidateDetails?.totalExperience?.months || '0 Month' }}
                                        </span>
                                    </div>
                                    <div>
                                        <span class="text-body-2 font-weight-regular">Relevant Exp - </span>
                                        <span class="text-body-2 font-weight-bold float-right">
                                            {{ interviewDetails?.candidateDetails?.relevantExperience?.years || '0 Year' }}
                                            {{ interviewDetails?.candidateDetails?.relevantExperience?.months || '0 Month' }}
                                        </span>
                                    </div>
                                    <div>
                                        <span class="text-body-2 font-weight-regular">Notice Period - </span>
                                        <span class="text-body-2 font-weight-regular float-right">
                                            {{ interviewDetails?.candidateDetails?.noticePeriod || 0 }} days
                                        </span>
                                    </div>
                                </div>
                            </v-col>
                            <v-col cols="2" class="feedback-summary-col">
                                <section class="feedback-score-container d-flex justify-center align-center flex-column py-2 px-3">
                                    <p class="mb-0 d-flex align-baseline">
                                        <span class="text-h5">{{ feedbackOverallScore.toFixed(1) }}</span>
                                        <span class="text-body-1">&nbsp;/&nbsp;10</span>
                                    </p>
                                    <span class="text-caption">Overall score</span>
                                </section>
                            </v-col>
                        </v-row>
                    </div>
                </v-card>
            </div>
            <div class="feedback-scroll-content pl-0 pr-4 pb-6">
                <InterviewScoreCard
                    v-if="feedbackDialogReady"
                    :readOnly="true"
                    :interViewFeedbackData="feedbackCurrentRound"
                    :tabsData="feedbackTabs"
                    :ratingScale='5'
                    :showTabs="showTabs"
                />
                <div v-else class="d-flex justify-center align-center pa-10 text-body-2 grey--text">
                    No feedback available for this interviewer.
                </div>
            </div>
            <div class="feedback-dialog-footer">
                <v-btn
                    depressed
                    outlined
                    rounded
                    class="btn outline-btn"
                    @click="dialog = false"
                >
                    Close
                </v-btn>
            </div>
        </v-card>
    </v-dialog>
</template>

<script>
import InterviewScoreCard from "@/components/InterviewScoreCard.vue";

export default {
    name: "InterviewFeedbackDialog",
    components: { InterviewScoreCard },
    props: {
        // v-model: controls dialog visibility
        value: { type: Boolean, default: false },
        // Candidate personal info ({ firstName, lastName, contactInfo: { email, phoneNumber } })
        personalInfo: { type: Object, default: null },
        // Interview details ({ interviewSlotDetails, candidateDetails })
        interviewDetails: { type: Object, default: null },
        // Candidate proctoring picture url
        proctoringPic: { type: String, default: null },
        // Full interview feedback array (all interviewers, all rounds)
        interViewFeedback: { type: Array, default: () => [] },
        // Email of the interviewer whose feedback should be shown
        interviewerEmail: { type: String, default: '' },
        // Round whose feedback should be shown
        round: { type: [Number, String], default: 1 },
    },
    data() {
        return {
            feedbackDialogReady: false,
            feedbackCurrentRound: [],
            feedbackTabs: { headers: [], data: [] },
            feedbackOverallScore: 0,
            showTabs: false,
            defaultProfilePic: require('@/assets/Profile 1.svg'),
        }
    },
    computed: {
        dialog: {
            get() {
                return this.value
            },
            set(val) {
                this.$emit('input', val)
            }
        },
        roundIndex() {
            return Number(this.round) - 1
        }
    },
    watch: {
        value(isOpen) {
            if (isOpen) {
                this.$nextTick(() => this.processFeedback())
            }
        },
        interViewFeedback() {
            if (this.value) this.processFeedback()
        },
        interviewerEmail() {
            if (this.value) this.processFeedback()
        },
        round() {
            if (this.value) this.processFeedback()
        }
    },
    methods: {
        processFeedback() {
            this.feedbackDialogReady = false
            this.feedbackCurrentRound = []
            this.feedbackTabs = { headers: [], data: [] }
            this.feedbackOverallScore = 0

            const feedbackList = this.interViewFeedback || []
            const activeRound = Number(this.round)
            const allRoundFeedback = feedbackList.filter(({ round }) => Number(round) === activeRound)
            const currentUserFeedback = allRoundFeedback.find(({ interviewerEmail }) => interviewerEmail == this.interviewerEmail)

            if (!currentUserFeedback) {
                return
            }

            const isNewFeedbackFormat = Object.keys(currentUserFeedback).includes('newFeedBack')
            const nameAndEmails = this.interviewDetails?.interviewSlotDetails?.[this.roundIndex]?.interviewerNameAndEmail || []
            this.feedbackCurrentRound = [{
                ...currentUserFeedback,
                name: nameAndEmails.find(ne => ne.email === currentUserFeedback.interviewerEmail)?.name
            }]

            if (!isNewFeedbackFormat) {
                this.showTabs = true
                this.buildFeedbackTabs(this.feedbackCurrentRound)
            } else {
                this.showTabs = false
                this.buildFeedbackTabsV2(this.feedbackCurrentRound)
            }
            this.calculateFeedbackOverallScore()
            this.$nextTick(() => {
                this.feedbackDialogReady = true
            })
        },
        calculateFeedbackOverallScore() {
            if (!this.feedbackCurrentRound || this.feedbackCurrentRound.length === 0) {
                this.feedbackOverallScore = 0
                return
            }
            let avgScore = this.feedbackCurrentRound.reduce((total, { avgScore }, _, arr) => total += Number(avgScore) / arr.length, 0)
            this.feedbackOverallScore = Number(avgScore.toFixed(2)) || 0
        },
        buildFeedbackTabs(interviewData = []) {
            this.feedbackTabs = { headers: [], data: [] }
            if (!interviewData.length) return
            // Core Skills is always the first tab — its data lives in `subjects`, not `skills`.
            this.feedbackTabs.headers = [{ name: 'Core Skills', totalfeedBackcount: 0, ScoredFeedbackCount: 0 }]
            interviewData[0].skills.forEach((item) => {
                if (item.skillName !== 'Core Skills') {
                    this.feedbackTabs.headers.push({ name: item.skillName, totalfeedBackcount: 0, ScoredFeedbackCount: 0 })
                }
            })
            let subObj = {}
            let skillObj = {}
            interviewData.forEach((item, index) => {
                item.subjects.forEach((coreSubject) => {
                    if (index === 0) {
                        subObj[coreSubject.subjectName] = coreSubject.feedback
                    } else {
                        this.pluckRatingTemp(coreSubject.subjectName, coreSubject.feedback, index, subObj)
                    }
                })
                item.skills.forEach((skill) => {
                    if (skill.skillName === 'Core Skills') return
                    if (index === 0) {
                        skillObj[skill.skillName] = skill.feedback
                    } else {
                        this.pluckRatingTemp(skill.skillName, skill.feedback, index, skillObj)
                    }
                })
            })
            let arr = []
            for (const key in subObj) {
                arr.push({ subName: key, data: subObj[key] })
            }
            this.feedbackTabs.data.push(arr)
            for (const key in skillObj) {
                this.feedbackTabs.data.push({ subName: key, data: skillObj[key] })
            }
        },
        buildFeedbackTabsV2(allInterviewersFeedback) {
            this.feedbackTabs = { headers: [], data: [] }
            if (!allInterviewersFeedback.length) return
            const mergedItems = allInterviewersFeedback[0].newFeedBack.map(item => ({ ...item }))
            allInterviewersFeedback.forEach((interviewer, index) => {
                if (index > 0) {
                    interviewer.newFeedBack.forEach((item, i) => {
                        if (mergedItems[i]) mergedItems[i]['rating' + index] = item.rating
                    })
                }
            })
            this.feedbackTabs = {
                headers: [{ name: 'Assessment', totalfeedBackcount: mergedItems.length, ScoredFeedbackCount: 0 }],
                data: [[{ subName: 'Assessment', data: mergedItems }]]
            }
        },
        pluckRatingTemp(key, feedback, outerIndex, obj) {
            feedback.forEach((item, index) => {
                if (obj[key] && obj[key][index]) {
                    obj[key][index]['rating' + outerIndex] = item.rating
                }
            })
        },
    }
}
</script>

<style scoped>
.feedback-fullscreen-card {
    height: 100vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}
.feedback-fixed-top {
    flex: 0 0 auto;
    background: #ffffff;
    z-index: 1;
}
.feedback-scroll-content {
    flex: 1 1 auto;
    overflow-y: auto;
    -ms-overflow-style: none;
    scrollbar-width: none;
    padding-bottom: 50px !important;
}
.feedback-scroll-content::-webkit-scrollbar {
    display: none;
}
.feedback-scroll-content ::v-deep .comment-container {
    background: transparent !important;
}
.feedback-summary-card {
    border-radius: 8px;
}
.feedback-summary-card .feedback-summary-col > div > div {
    padding: 2px 0;
}
.feedback-summary-card .feedback-summary-col .text-body-2.font-weight-regular {
    color: rgba(0, 0, 0, 0.6);
}
.feedback-user-list {
    list-style: none;
    margin: 0 0 0 12px;
    padding: 0;
}
.feedback-summary-col {
    border-right: 1px solid #0000001F;
}
.feedback-summary-col:last-child {
    border-right: none;
}
.feedback-score-container {
    border: 1px solid #2bb673;
    border-radius: 8px;
    background: #e9fff2;
    min-width: 120px;
}
.feedback-dialog-footer {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    padding: 12px 24px;
    background: #ffffff;
    border-top: 1px solid #0000001F;
    display: flex;
    justify-content: flex-end;
    z-index: 10;
}
.w-90 {
    width: 90%;
}
</style>
